import { useCallback, useEffect, useRef } from "react";

import { useLogger } from "@/sdk/logging/LoggerProvider";

import type { Task } from "../../agent";

import { useAgentSdk } from "../../agent/hooks";
import { getSessionByTaskId } from "../../sessions";
import { useSessionStore } from "../session.store";

const getTimeRemaining = (expirationTimestamp: number) => Math.max(expirationTimestamp - Date.now(), 0);

/**
 * A hook to manage wrapping state timers for concurrent sessions
 * Schedules session wrapup expiration based on session wrapping state
 * Automatically completes wrapup for tasks where wrapping time has expired
 * Handles race condition between task status changes and session wrapping state
 */
export function useWrapupTimer() {
    const logger = useLogger({ module: "useWrapupTimer" });
    const timeoutMap = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    const agentSdk = useAgentSdk();

    // Track tasks in wrapping state to handle session updates
    const wrappingTasksRef = useRef<Map<string, Task>>(new Map());
    // Track session wrapping state to optimize comparison
    const sessionWrappingStateRef = useRef(new Map<string, number | undefined>());

    /**
     * For a given task, returns the time remaining for its wrapping state.
     * Returns 0 if no session or no expiration timestamp or if the timer has already expired
     */
    const getWrappingTimeRemaining = useCallback((task: Task) => {
        const session = getSessionByTaskId(task.id);
        if (!session || !session.wrappingState?.expirationTimestamp) {
            return 0;
        }
        return getTimeRemaining(session.wrappingState.expirationTimestamp);
    }, []);

    const cleanupTimeout = useCallback((sessionId: string): void => {
        const timeout = timeoutMap.current.get(sessionId);
        if (timeout) {
            clearTimeout(timeout);
            timeoutMap.current.delete(sessionId);
        }
    }, []);

    const endTaskWrapup = useCallback(
        async (task: Task): Promise<void> => {
            const session = getSessionByTaskId(task.id);
            if (!session) {
                logger.warn({ taskId: task.id }, "Cannot end wrapup - no session found for task");
                return;
            }
            await agentSdk.endWrapup("WrappingTimeExpired", task.partner, session.id, task);
        },
        [agentSdk, logger],
    );

    const scheduleEndWrapup = useCallback(
        (task: Task): void => {
            const session = getSessionByTaskId(task.id);
            const taskLogger = logger.child({ taskId: task.id, sessionId: session?.id });

            if (!session) {
                taskLogger.warn("Cannot schedule wrapup - no session found for task");
                return;
            }

            if (timeoutMap.current.has(session.id)) {
                taskLogger.debug("Wrapup already scheduled for session - skipping duplicate");
                return;
            }

            // Handle missing wrapping state (can happen after refresh/rehydration)
            if (!session.wrappingState?.expirationTimestamp) {
                if (task.status !== "wrapping") {
                    taskLogger.debug({ taskStatus: task.status }, "Task not in wrapping state - skipping");
                    return;
                }
                taskLogger.info(
                    { taskStatus: task.status, wrappingDuration: task.wrappingDuration },
                    "Task in wrapping state but no expiration timestamp - expired/rehydrated task, cleaning up immediately",
                );

                wrappingTasksRef.current.delete(task.id);
                void endTaskWrapup(task);
                return;
            }

            const timeRemaining = getTimeRemaining(session.wrappingState.expirationTimestamp);
            const isAlreadyExpired = timeRemaining <= 0;

            taskLogger.info(
                {
                    timeRemaining,
                    expirationTimestamp: session.wrappingState.expirationTimestamp,
                    isAlreadyExpired,
                    taskStatus: task.status,
                },
                isAlreadyExpired ? "Timer already expired - completing immediately" : "Scheduling wrapup timer",
            );

            if (isAlreadyExpired) {
                wrappingTasksRef.current.delete(task.id);
                void endTaskWrapup(task);
                return;
            }

            timeoutMap.current.set(
                session.id,
                setTimeout(() => {
                    taskLogger.info("Wrapup timer fired - completing task");
                    void endTaskWrapup(task).finally(() => timeoutMap.current.delete(session.id));
                }, timeRemaining),
            );
        },
        [endTaskWrapup, logger],
    );

    const cleanupInactiveSessions = useCallback(
        (activeSessionIds: Set<string>): void => {
            Array.from(timeoutMap.current.keys()).forEach((sessionId) => {
                if (!activeSessionIds.has(sessionId)) {
                    cleanupTimeout(sessionId);

                    wrappingTasksRef.current.forEach((task, taskId) => {
                        const session = getSessionByTaskId(task.id);
                        if (!session || session.id === sessionId) {
                            wrappingTasksRef.current.delete(taskId);
                        }
                    });

                    sessionWrappingStateRef.current.delete(sessionId);
                }
            });
        },
        [cleanupTimeout],
    );

    /**
     * Schedules wrapup for tasks that are currently in the wrapping state.
     * Cleans up any existing timers for sessions that are no longer active.
     */
    const scheduleWrappingForTasks = useCallback(
        (tasks: Task[]) => {
            const wrappingTasks = tasks.filter((task) => task.status === "wrapping");
            const scheduleLogger = logger.child({ wrappingTaskCount: wrappingTasks.length });

            if (wrappingTasks.length > 0) {
                scheduleLogger.debug(
                    { taskIds: wrappingTasks.map((t) => t.id) },
                    "Scheduling wrapup for wrapping tasks",
                );
            }

            const activeSessionIds = new Set<string>();

            wrappingTasksRef.current.clear();
            sessionWrappingStateRef.current.clear();

            wrappingTasks.forEach((task) => {
                wrappingTasksRef.current.set(task.id, task);

                const session = getSessionByTaskId(task.id);
                if (!session) {
                    scheduleLogger
                        .child({ taskId: task.id })
                        .info("Wrapping task has no session - expired/rehydrated task, cleaning up immediately");
                    wrappingTasksRef.current.delete(task.id);
                    void endTaskWrapup(task);
                    return;
                }

                sessionWrappingStateRef.current.set(session.id, session.wrappingState?.expirationTimestamp);
                activeSessionIds.add(session.id);

                scheduleEndWrapup(task);
            });

            cleanupInactiveSessions(activeSessionIds);
        },
        [scheduleEndWrapup, cleanupInactiveSessions, endTaskWrapup, logger],
    );

    const scheduleSpecificTasks = useCallback(
        (tasksToSchedule: Task[]) => {
            if (tasksToSchedule.length === 0) return;

            const rescheduleLogger = logger.child({ taskCount: tasksToSchedule.length });
            rescheduleLogger.debug(
                { taskIds: tasksToSchedule.map((t) => t.id) },
                "Rescheduling tasks due to wrapping state change",
            );

            tasksToSchedule.forEach((task) => {
                const session = getSessionByTaskId(task.id);
                if (!session) {
                    logger.child({ taskId: task.id }).warn("Cannot reschedule - no session found");
                    return;
                }

                sessionWrappingStateRef.current.set(session.id, session.wrappingState?.expirationTimestamp);

                // Clear any existing timeout for this session before scheduling a new one
                if (timeoutMap.current.has(session.id)) {
                    cleanupTimeout(session.id);
                }

                scheduleEndWrapup(task);
            });
        },
        [scheduleEndWrapup, cleanupTimeout, logger],
    );

    // ! important ! Listening for session store updates to handle race conditions
    useEffect(() => {
        // Track session wrapping state changes for each wrapping task
        const unsubscribe = useSessionStore.subscribe((state, previousState) => {
            if (wrappingTasksRef.current.size === 0) return;

            const changedTasks: Task[] = [];

            // Check each wrapping task for relevant changes
            wrappingTasksRef.current.forEach((task) => {
                const currentSession = state.getSessionByTaskId?.(task.id);
                const previousSession = previousState.getSessionByTaskId?.(task.id);

                const currentExpTimestamp = currentSession?.wrappingState?.expirationTimestamp;
                const prevExpTimestamp = previousSession?.wrappingState?.expirationTimestamp;

                // If the expiration timestamp has changed, we need to reschedule
                if (currentExpTimestamp && currentExpTimestamp !== prevExpTimestamp) {
                    changedTasks.push(task);
                }
            });

            if (changedTasks.length > 0) {
                scheduleSpecificTasks(changedTasks);
            }
        });

        return unsubscribe;
    }, [scheduleSpecificTasks]);

    // Clean up all timers when component unmounts
    useEffect(() => {
        const timeoutsToCleanup = timeoutMap.current;
        const wrappingTasksToCleanup = wrappingTasksRef.current;
        const sessionStateToCleanup = sessionWrappingStateRef.current;

        return () => {
            timeoutsToCleanup.forEach((timeout) => {
                clearTimeout(timeout);
            });
            timeoutsToCleanup.clear();
            wrappingTasksToCleanup.clear();
            sessionStateToCleanup.clear();
        };
    }, []);

    return {
        getWrappingTimeRemaining,
        scheduleEndWrapup,
        scheduleWrappingForTasks,
    };
}
